// No direct PHP counterpart.
// Shared JS helper for the list detail controllers:
// - api/fuel/app/classes/controller/api/monty/users/listing.php
// - api/fuel/app/classes/controller/api/monty/groups/listing.php
// - api/fuel/app/classes/controller/api/monty/buddies/listing.php
// Role in the port:
// - keep users/group/buddies list-detail endpoints as thin controller wrappers
// - centralize offline-only owner/list/edition resolution and permission assembly
import DB from 'offline/db'
import F from 'offline/filter/constants'
import ListingFilter from 'offline/filter/listing'
import Listing from 'offline/model/listing'
import ListingEdition from 'offline/model/listing/edition'
import { buildFilterConfig, buildOwnerPermissions } from 'offline/api/monty'

var LIST_TYPE_AUTOMATED = 1
var LIST_TYPE_MANUAL = 2

function ownerField(ownerType) {
	if (ownerType === F.OWNER_USER) return 'user_id'
	if (ownerType === F.OWNER_GROUP) return 'group_id'
	if (ownerType === F.OWNER_BUDDIES) return 'buddies_id'
	return null
}

function currentUserId(params) {
	return params.sessionUserId || Number(DB.meta('sync_user_id') || 0) || null
}

function loadListing(owner, ownerType, params) {
	var field = ownerField(ownerType)
	if (!field || !params.list) return null

	return Listing.record({
		where: [
			[field, '=', owner.get_id()],
			['list_slug', '=', params.list]
		]
	})
}

function loadEdition(listing, ownerType, params) {
	if (params.edition) {
		return ListingEdition.record({
			where: [
				['list_edition_slug', '=', params.edition],
				['list_id', '=', listing.get_id()]
			]
		})
	}

	var current = ListingEdition.record({
		where: [
			['list_edition_current', '=', 1],
			['list_id', '=', listing.get_id()]
		]
	})

	if (current || ownerType !== F.OWNER_USER) return current

	var editions = ListingEdition.filter({
		where: [['list_id', '=', listing.get_id()]],
		order: { list_edition_date: 'desc' },
		limit: 1
	})

	return editions.length ? editions[0] : null
}

function canViewListing(listing, owner, ownerType, sessionUserId) {
	if (listing.get_public()) return true

	if (ownerType === F.OWNER_USER) {
		return !!sessionUserId && listing.get_user_id() === sessionUserId
	}

	if (ownerType === F.OWNER_GROUP) {
		return !!sessionUserId && DB.scalar(
			'SELECT COUNT(*) FROM link_group_user WHERE group_id = ? AND user_id = ?',
			[owner.get_id(), sessionUserId]
		) > 0
	}

	return ownerType === F.OWNER_BUDDIES
}

function buildListingPermissions(listing, owner, ownerType, sessionUserId) {
	var permissions = buildOwnerPermissions(owner, ownerType, sessionUserId)

	if (!sessionUserId || listing.get_user_id() !== sessionUserId) return permissions

	permissions.list = { create: false, edit: false, delete: false }
	permissions.edition = { create: false, edit: false, delete: false }
	permissions.list.create = listing.get_type() !== LIST_TYPE_AUTOMATED
	permissions.list.edit = !listing.get_template()
	permissions.list.delete = true
	permissions.edition.create = listing.get_type() !== LIST_TYPE_AUTOMATED
	permissions.edition.edit = !listing.get_template() && listing.get_type() !== LIST_TYPE_AUTOMATED
	permissions.edition.delete = !listing.get_template() && listing.get_type() !== LIST_TYPE_AUTOMATED && listing.get_count_editions() > 1

	return permissions
}

export function runListingEndpoint(params, ownerType) {
	var sessionUserId = currentUserId(params)
	var config = buildFilterConfig(params, ownerType)
	var listing
	var edition
	var response
	var hasPreviewFilter = !!(params.filter && Object.keys(params.filter).length)
	var requestedFilter

	if (!config) {
		return Promise.reject({ error: 'Owner not found in offline database', offline: true })
	}

	listing = loadListing(config.owner, ownerType, params)
	if (!listing) {
		return Promise.reject({ error: 'List not found', offline: true })
	}

	if (!canViewListing(listing, config.owner, ownerType, sessionUserId)) {
		return Promise.reject({ error: 'List not found', offline: true })
	}

	edition = loadEdition(listing, ownerType, params)
	if (!edition) {
		return Promise.reject({ error: 'List edition not found', offline: true })
	}

	requestedFilter = params.filter !== undefined ? params.filter : (listing.get_filter() || {})

	response = ListingFilter.forge({
		owner: config.owner,
		cache: params.key || false,
		filter: requestedFilter,
		page: params.page || 1,
		inclusive: params.pagesInclusive || false,
		knownUsers: params.knownUsers || [],
		preview: hasPreviewFilter,
		perpage: listing.get_type() === LIST_TYPE_MANUAL ? 1000 : F.PERPAGE,
		listing: listing,
		edition: edition,
		sessionID: sessionUserId,
		userID: ownerType === F.OWNER_USER ? config.owner.get_id() : sessionUserId,
		settings: { session: {}, owner: {} },
		can: {}
	}).run().to_array({
		status: 1,
		session: { offline: true },
		permissions: buildListingPermissions(listing, config.owner, ownerType, sessionUserId)
	})

	return Promise.resolve(response)
}

export default {
	runListingEndpoint: runListingEndpoint
}
